<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, provide, ref, useTemplateRef, watch} from "vue";
import {Screen} from "quasar";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {gsap} from "gsap";
import Translator from "@/utils/common/Translator.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {dashboardPresenterKey, DashboardProps} from "@/utils/presenters/interfaces.ts";
import {
  getDashboardWidgetComponent,
  getDashboardWidgetDefinition,
  hasOwnOpenWidgetButton,
  isAutoHeightWidget,
  isEChartWidget,
  sortChartLines,
  widgetGridArea
} from "@/components/dashboardWidgets/dashboardWidgetRegistry.ts";
import CrossFilterBar from "@/components/bi/CrossFilterBar.vue";
import WidgetLocalFilterButton from "@/components/bi/WidgetLocalFilterButton.vue";
import DrillDownDialog from "@/components/bi/DrillDownDialog.vue";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import DashboardUtils from "@/utils/common/DashboardUtils.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import type {BIDrillDownByClick, BIDrillDownClick} from "@/components/bi/biChartTypes.ts";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import {useDashboardViewOptionsStore} from "@/stores/dashboardViewOptionsStore.ts";
import {useRouter} from "vue-router";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import {QuestionFieldsSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import DTOUserDashbordChartLine = namaTypes.DTOUserDashbordChartLine;
import DTOBIChartLine = namaTypes.DTOBIChartLine;

interface DashboardEditProps extends DashboardProps {
  editMode?: boolean;
  editModeLocked?: boolean;
}

const props = defineProps<DashboardEditProps>();

const emit = defineEmits<{
  editModeChanged: [positions: DTOUserDashbordChartLine[]];
}>();

const isEditMode = ref(props.editMode ?? false);

// Drag state for edit mode
const draggingLine = ref<DTOUserDashbordChartLine | null>(null);
const dragStartX = ref(0);
const dragStartY = ref(0);
const dragOrigCol = ref(0);
const dragOrigRow = ref(0);
const resizingLine = ref<DTOUserDashbordChartLine | null>(null);
const resizeDirection = ref<'se' | 'e' | 's' | 'n' | 'w' | 'nw' | 'ne' | 'sw'>('se');
const resizeOrigCols = ref(0);
const resizeOrigRows = ref(0);

provide(dashboardPresenterKey, props.presenter);

const chartKey = ref<number>(0);
const timelineAnimation = gsap.timeline();
const chartKeyComputed = computed(() => chartKey.value + '');
const {darkMode} = storeToRefs(useNamaFlagsStore());
const isRefreshClicked = ref(false);

watch(darkMode, async () => {
  chartKey.value++;
  await nextTick();
  animateDashboardWidgets();
});

const COLLAPSED_ROW_HEIGHT = 36;

const chartLines = computed<DTOBIChartLine[]>(() => {
  const charts = sortChartLines(props.presenter.fetchedData.value?.chartLines);
  if (charts.length)
    return charts
  else
    return [{
      chartType: "RecentVisits",
      widgetId: GUIUtilities.RECENT_VISITS_KEY,
      colNumber: -3,
      cols: 6,
      rowNumber: 1,
      rows: 2
    }]
});
const dashboardContainerRef = useTemplateRef<HTMLDivElement>('dashboardContainerRef');
const gridRef = useTemplateRef<HTMLDivElement>('gridRef');

function calcMobileMaxRowsPrecent() {
  if (props.presenter.fetchedData.value?.mobileMaxRowsCount && props.presenter.fetchedData.value?.mobileMaxRowsCount > 0)
    return 100 / (props.presenter.fetchedData.value?.mobileMaxRowsCount);
  return 100;
}

function calcRowHeight() {
  if (!dashboardContainerRef.value)
    return
  const rows = props.presenter.fetchedData.value?.rowsCount || 1;
  return dashboardContainerRef.value.offsetHeight / rows;
}

function toAnimate(elements: NodeListOf<Element>, options?: GSAPTimelineVars, startAnimateAfterPreviousAnimation: boolean = false): void {
  if (!elements.length)
    return;
  timelineAnimation.to(elements,
    {
      y: 0,
      opacity: 1,
      ease: "power4.out",
      duration: 0.6,
      ...options,
    }, (startAnimateAfterPreviousAnimation ? ">" : "<"))
}

function animateDashboardWidgets() {
  if (!dashboardContainerRef.value)
    return;
  const boxes = dashboardContainerRef.value.querySelectorAll("[data-animate-box]");
  const text = dashboardContainerRef.value.querySelectorAll("[data-animate-text]");
  const icons = dashboardContainerRef.value.querySelectorAll("[data-animate-icon]")
  const badges = dashboardContainerRef.value.querySelectorAll("[data-animate-badge]")

  toAnimate(boxes, {
    stagger: .012,
    delay: .16
  })
  toAnimate(text, {
    stagger: .01
  })
  toAnimate(icons)
  toAnimate(badges, {
    duration: 1,
    delay: 0.2
  }, true)
}

const crossFilterQuestionFields = computed(() => {
  return (props.presenter.fetchedData.value as any)?.crossFilterQuestionFields ?? [];
});

const isTabbedDashboard = computed(() => {
  return props.presenter.fetchedData.value?.kind === 'Tabbed';
});

interface TabEntry {
  subDashboardId: string;
  label: string;
}

const dashboardTabs = computed<TabEntry[]>(() => {
  const tabs = props.presenter.fetchedData.value?.tabs ?? [];
  return tabs.map((t: any) => ({
    subDashboardId: t.subDashboardId,
    label: tabLabelFor(t),
  }));
});

function tabLabelFor(tab: any): string {
  if (Translator.isArabic())
    return tab.arTitle || tab.name1 || tab.code || '';
  return tab.enTitle || tab.name2 || tab.name1 || tab.code || '';
}

const activeTabId = computed({
  get: () => props.presenter.activeSubDashboardId.value ?? '',
  set: (value: string) => {
    props.presenter.activeSubDashboardId.value = value;
  },
});

async function onTabSwitched(subDashboardId: string) {
  if (!subDashboardId) return;
  await props.presenter.switchTab(subDashboardId);
  syncActiveTabToURL();
  chartKey.value++;
  await nextTick();
  animateDashboardWidgets();
}

function syncActiveTabToURL() {
  if (isEditMode.value) return;
  const query = {...router.currentRoute.value.query};
  const activeId = props.presenter.activeSubDashboardId.value;
  if (activeId)
    query[PlaceTokens.PRM_SUB_DASHBOARD] = activeId;
  else
    delete query[PlaceTokens.PRM_SUB_DASHBOARD];
  router.replace({query});
}

const crossFilterStore = useCrossFilterStore();
const router = useRouter();
const cfSerializer = new QuestionFieldsSerializer();

async function onChartCrossFilterClicked(emissions: any[]) {
  const sourceChartId = emissions?.[0]?.sourceChartId ?? "";
  crossFilterStore.emitFromChart(sourceChartId, emissions);
  await onCrossFiltersChanged();
}

const drillDownClick = ref<BIDrillDownClick | undefined>();
const drillDownByClick = ref<BIDrillDownByClick | undefined>();
const drillDownDialogOpen = ref<boolean>(false);

function onDrillDownRequested(click: BIDrillDownClick) {
  drillDownClick.value = click;
  drillDownByClick.value = undefined;
  drillDownDialogOpen.value = true;
}

function onDrillDownByRequested(click: BIDrillDownByClick) {
  drillDownByClick.value = click;
  drillDownClick.value = undefined;
  drillDownDialogOpen.value = true;
}

async function onCrossFiltersChanged() {
  syncCrossFiltersToURL();
  await props.presenter.doFetchCharts(props.presenter.dashboardId, true, false);
  chartKey.value++;
  await nextTick();
  animateDashboardWidgets();
}

function syncCrossFiltersToURL() {
  if (isEditMode.value) return;
  const filled = crossFilterStore.getFilledQuestionFields();
  const query = {...router.currentRoute.value.query};
  if (filled.length > 0)
    query[PlaceTokens.PRM_CROSS_FILTERS] = cfSerializer.toURL(filled);
  else
    delete query[PlaceTokens.PRM_CROSS_FILTERS];
  router.replace({query});
}

async function onRefreshClicked() {
  isRefreshClicked.value = true;
  await props.presenter.doFetchCharts(props.presenter.dashboardId, true, false);
  chartKey.value++;
  await nextTick();
  animateDashboardWidgets();
  isRefreshClicked.value = false;
}

const viewOptionsStore = useDashboardViewOptionsStore();
viewOptionsStore.initCollapsedForDashboard(props.presenter.dashboardId);

watch(() => props.presenter.dashboardId, (id) => {
  viewOptionsStore.initCollapsedForDashboard(id);
});

function effectiveRows(line: DTOUserDashbordChartLine): number {
  if (isEditMode.value) return line.rows ?? 1;
  return viewOptionsStore.isCollapsed(line.widgetId) ? 1 : (line.rows ?? 1);
}

function isLineCollapsed(line: DTOUserDashbordChartLine): boolean {
  if (isEditMode.value) return false;
  return viewOptionsStore.isCollapsed(line.widgetId);
}

function canShowFloatingCollapse(line: DTOUserDashbordChartLine): boolean {
  return viewOptionsStore.showToolbox && !isEChartWidget(line) && line.chartType !== 'CrossFilterControl';
}

function canShowFloatingOpenWidget(line: DTOUserDashbordChartLine): boolean {
  return showAdminToolbox.value && !!line.widgetId && line.widgetId !== GUIUtilities.RECENT_VISITS_KEY
    && !hasOwnOpenWidgetButton(line);
}

function openWidgetEditView(line: DTOUserDashbordChartLine) {
  if (!line.widgetId) return;
  const link = PlaceUtils.editLink('DashBoardWidget', line.widgetId);
  if (link) window.open(link, '_blank');
}

function canShowRefreshButton(): boolean {
  let length = props.presenter.fetchedData.value?.chartLines?.length ?? -1;
  const hasChartLines = length > 0;
  return !props.isReportType && hasChartLines
}

const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());
const showAdminToolbox = computed(() => isAdmin.value && viewOptionsStore.showToolbox);

function openDashboardEditView() {
  const id = props.presenter.dashboardId;
  if (!id) return;
  const link = PlaceUtils.editLink('DashBoard', id);
  if (link) window.open(link, '_blank');
}

function getEffectiveRowsCount(): number {
  const declared = props.presenter.fetchedData.value?.rowsCount ?? 1;
  const lines = props.presenter.fetchedData.value?.chartLines ?? [];
  const collapsedSet = viewOptionsStore.collapsedWidgets;
  let maxBottom = 0;
  for (const l of lines) {
    const span = collapsedSet.has(l.widgetId ?? '') ? 1 : (l.rows ?? 1);
    const bottom = (l.rowNumber ?? 1) + span - 1;
    if (bottom > maxBottom) maxBottom = bottom;
  }
  return Math.max(declared, maxBottom);
}

type RowKind = 'collapsed' | 'fixed' | 'auto';

const rowKinds = computed<RowKind[]>(() => {
  if (props.isReportType || Screen.lt.sm || isEditMode.value)
    return [];
  if (props.isEditScreen && !isEditMode.value)
    return [];
  const lines = props.presenter.fetchedData.value?.chartLines ?? [];
  const rowsCount = getEffectiveRowsCount();
  const collapsedSet = viewOptionsStore.collapsedWidgets;
  const kinds: RowKind[] = [];
  for (let r = 1; r <= rowsCount; r++) {
    const widgetsInRow = lines.filter(l => {
      const start = l.rowNumber ?? 1;
      const span = collapsedSet.has(l.widgetId ?? '') ? 1 : (l.rows ?? 1);
      return start <= r && r < start + span;
    });
    if (widgetsInRow.length === 0) {
      kinds.push(collapsedSet.size > 0 ? 'collapsed' : 'fixed');
      continue;
    }
    const visibleWidgets = widgetsInRow.filter(l => !collapsedSet.has(l.widgetId ?? ''));
    if (visibleWidgets.length === 0) {
      kinds.push('collapsed');
      continue;
    }
    const hasResponsive = visibleWidgets.some(l => !isAutoHeightWidget(l));
    kinds.push(hasResponsive ? 'fixed' : 'auto');
  }
  return kinds;
});

const dashboardContainerStyle = computed(() => {
  if (props.isReportType)
    return {};
  const gridTemplateColumns: string = Screen.lt.sm ? '' : 'repeat(' + (props.presenter.fetchedData.value?.colsCount ?? 1) + ', minmax(0, 1fr))';
  if (Screen.lt.sm || (props.isEditScreen && !isEditMode.value))
    return {gridTemplateColumns, gridAutoRows: 'auto', minHeight: Screen.lt.sm ? '100%' : undefined};
  if (isEditMode.value)
    return {gridTemplateColumns, gridAutoRows: (calcRowHeight() ?? 100) + 'px'};
  const kinds = rowKinds.value;
  if (kinds.length === 0) return {gridTemplateColumns};
  const screenH = containerHeight.value || dashboardContainerRef.value?.offsetHeight || 600;
  const rowsPerPage = props.presenter.fetchedData.value?.rowsCount || 1;
  const totalRows = kinds.length;
  const measured = measuredAutoRowHeights.value;
  const rowValues: string[] = [];
  for (let pageStart = 0; pageStart < totalRows; pageStart += rowsPerPage) {
    const pageEnd = Math.min(pageStart + rowsPerPage, totalRows);
    const pageSize = pageEnd - pageStart;
    const pageBudget = screenH * (pageSize / rowsPerPage);
    let consumedHeight = 0;
    let responsiveCount = 0;
    for (let i = pageStart; i < pageEnd; i++) {
      if (kinds[i] === 'collapsed')
        consumedHeight += COLLAPSED_ROW_HEIGHT;
      else if (kinds[i] === 'auto')
        consumedHeight += (measured[i] || 0);
      else responsiveCount++;
    }
    const responsiveHeight = responsiveCount > 0 ? Math.round((pageBudget - consumedHeight) / responsiveCount) : 0;
    for (let i = pageStart; i < pageEnd; i++) {
      if (kinds[i] === 'collapsed') rowValues.push(COLLAPSED_ROW_HEIGHT + 'px');
      else if (kinds[i] === 'auto') rowValues.push('auto');
      else rowValues.push(responsiveHeight + 'px');
    }
  }
  return {gridTemplateColumns, gridTemplateRows: rowValues.join(' ')};
})

const containerHeight = ref(0);
const measuredAutoRowHeights = ref<number[]>([]);
let gridObserver: ResizeObserver | null = null;

function measureGrid() {
  const scrollEl = dashboardContainerRef.value;
  const gridEl = gridRef.value;
  if (!scrollEl) return;
  const newH = scrollEl.clientHeight;
  if (Math.abs(newH - containerHeight.value) > 2)
    containerHeight.value = newH;
  if (!gridEl) return;
  const kinds = rowKinds.value;
  if (kinds.length === 0) return;
  const resolved = getComputedStyle(gridEl).gridTemplateRows;
  if (!resolved || resolved === 'none') return;
  const heights = resolved.split(/\s+/).map(v => parseFloat(v) || 0);
  const newMeasured = kinds.map((k, i) => k === 'auto' ? (heights[i] || 0) : 0);
  const prev = measuredAutoRowHeights.value;
  if (newMeasured.length !== prev.length || newMeasured.some((h, i) => Math.abs(h - (prev[i] || 0)) > 2))
    measuredAutoRowHeights.value = newMeasured;
}

function setupObservers() {
  gridObserver?.disconnect();
  const scrollEl = dashboardContainerRef.value;
  const gridEl = gridRef.value;
  if (!scrollEl) return;
  gridObserver = new ResizeObserver(() => measureGrid());
  gridObserver.observe(scrollEl);
  if (gridEl)
    gridEl.querySelectorAll(':scope > [data-widget-id]').forEach(cell => gridObserver!.observe(cell));
}

onMounted(async () => {
  setupObservers();
  measureGrid();
  await NamaCommon.waitFor(250);
  measureGrid();
  animateDashboardWidgets();
})

onUnmounted(() => {
  gridObserver?.disconnect();
})

watch(chartKey, async () => {
  await nextTick();
  setupObservers();
  measureGrid();
});

// --- Edit mode logic ---

const dragTransformX = ref(0);
const dragTransformY = ref(0);
const END_DIRS = new Set(['se', 'e', 'ne']);
const START_DIRS = new Set(['sw', 'w', 'nw']);
const BOTTOM_DIRS = new Set(['se', 's', 'sw']);
const TOP_DIRS = new Set(['ne', 'n', 'nw']);
let savedBodyCursor = '';

function getWidgetTitle(line: DTOUserDashbordChartLine): string {
  return DashboardUtils.calcChartTitle(line) || line.widgetId || '';
}

function getColsCount(): number {
  return props.presenter.fetchedData.value?.colsCount ?? 6;
}

function getCellDimensions(): {cellWidth: number; cellHeight: number} {
  if (!dashboardContainerRef.value) return {cellWidth: 100, cellHeight: 100};
  const rect = dashboardContainerRef.value.getBoundingClientRect();
  const cellWidth = rect.width / getColsCount();
  const rowsCount = props.presenter.fetchedData.value?.rowsCount ?? 4;
  const cellHeight = rect.height / rowsCount;
  return {cellWidth, cellHeight: cellHeight > 0 ? cellHeight : 100};
}

function getDragStyle(line: DTOUserDashbordChartLine): Record<string, string> {
  if (draggingLine.value === line)
    return {transform: `translate(${dragTransformX.value}px, ${dragTransformY.value}px)`, zIndex: '50', opacity: '0.85', transition: 'none'};
  return {};
}

// --- Collision detection ---

interface WidgetRect { row: number; col: number; rows: number; cols: number; }

function getRect(line: DTOUserDashbordChartLine): WidgetRect {
  return {row: line.rowNumber ?? 1, col: line.colNumber ?? 1, rows: line.rows ?? 1, cols: line.cols ?? 1};
}

function rectsOverlap(a: WidgetRect, b: WidgetRect): boolean {
  return a.col < b.col + b.cols && a.col + a.cols > b.col &&
         a.row < b.row + b.rows && a.row + a.rows > b.row;
}

let lastPushedWidgetId: string | undefined;

function resolveCollisions(movedLine: DTOUserDashbordChartLine) {
  const lines = props.presenter.fetchedData.value?.chartLines;
  if (!lines) return;
  const movedRect = getRect(movedLine);
  lastPushedWidgetId = undefined;
  lowestPushedBottom = 0;

  const others = lines.filter(l => l !== movedLine).sort((a, b) => (a.rowNumber ?? 1) - (b.rowNumber ?? 1));

  for (const other of others) {
    const otherRect = getRect(other);
    if (!rectsOverlap(movedRect, otherRect)) continue;
    other.rowNumber = movedRect.row + movedRect.rows;
    const bottomEdge = (other.rowNumber ?? 1) + (other.rows ?? 1);
    if (bottomEdge > lowestPushedBottom) {
      lowestPushedBottom = bottomEdge;
      lastPushedWidgetId = (other as any).widgetId;
    }
    resolveCollisionsForPushed(other, lines);
  }
}

let lowestPushedBottom = 0;

function resolveCollisionsForPushed(pushedLine: DTOUserDashbordChartLine, lines: DTOUserDashbordChartLine[]) {
  const pushedRect = getRect(pushedLine);
  for (const other of lines) {
    if (other === pushedLine) continue;
    const otherRect = getRect(other);
    if (!rectsOverlap(pushedRect, otherRect)) continue;
    other.rowNumber = pushedRect.row + pushedRect.rows;
    const bottomEdge = (other.rowNumber ?? 1) + (other.rows ?? 1);
    if (bottomEdge > lowestPushedBottom) {
      lowestPushedBottom = bottomEdge;
      lastPushedWidgetId = (other as any).widgetId;
    }
    resolveCollisionsForPushed(other, lines);
  }
}

function scrollToWidget(widgetId: string | undefined) {
  if (!widgetId || !dashboardContainerRef.value) return;
  nextTick(() => {
    const el = dashboardContainerRef.value?.querySelector(`[data-widget-id="${widgetId}"]`);
    if (el) el.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  });
}

// --- Cursor management ---

function resizeCursorFor(direction: string): string {
  const isRTL = Translator.isArabic();
  const map: Record<string, string> = {
    se: isRTL ? 'sw-resize' : 'se-resize',
    sw: isRTL ? 'se-resize' : 'sw-resize',
    ne: isRTL ? 'nw-resize' : 'ne-resize',
    nw: isRTL ? 'ne-resize' : 'nw-resize',
    e: 'ew-resize', w: 'ew-resize',
    n: 'ns-resize', s: 'ns-resize',
    move: 'move'
  };
  return map[direction] ?? 'default';
}

function lockCursor(direction: string) {
  savedBodyCursor = document.body.style.cursor;
  document.body.style.cursor = resizeCursorFor(direction);
  document.body.style.userSelect = 'none';
}

function unlockCursor() {
  document.body.style.cursor = savedBodyCursor;
  document.body.style.userSelect = '';
}

// --- Drag ---

function onEditDragStart(line: DTOUserDashbordChartLine, e: MouseEvent) {
  if (!isEditMode.value) return;
  e.preventDefault();
  draggingLine.value = line;
  dragStartX.value = e.clientX;
  dragStartY.value = e.clientY;
  dragOrigCol.value = line.colNumber ?? 1;
  dragOrigRow.value = line.rowNumber ?? 1;
  dragTransformX.value = 0;
  dragTransformY.value = 0;
  lockCursor('move');
  document.addEventListener('mousemove', onEditDragMove);
  document.addEventListener('mouseup', onEditDragEnd);
}

function onEditDragMove(e: MouseEvent) {
  if (!draggingLine.value) return;
  dragTransformX.value = e.clientX - dragStartX.value;
  dragTransformY.value = e.clientY - dragStartY.value;
}

function onEditDragEnd() {
  if (draggingLine.value) {
    const {cellWidth, cellHeight} = getCellDimensions();
    const hDir = Translator.isArabic() ? -1 : 1;
    const deltaCol = Math.round((hDir * dragTransformX.value) / cellWidth);
    const deltaRow = Math.round(dragTransformY.value / cellHeight);
    const maxCol = getColsCount() - (draggingLine.value.cols ?? 1) + 1;
    draggingLine.value.colNumber = Math.max(1, Math.min(maxCol, dragOrigCol.value + deltaCol));
    draggingLine.value.rowNumber = Math.max(1, dragOrigRow.value + deltaRow);
    dragTransformX.value = 0;
    dragTransformY.value = 0;
    resolveCollisions(draggingLine.value);
    compactEmptyRows();
    scrollToWidget((draggingLine.value as any).widgetId);
  }
  draggingLine.value = null;
  unlockCursor();
  document.removeEventListener('mousemove', onEditDragMove);
  document.removeEventListener('mouseup', onEditDragEnd);
}

// --- Resize ---

let resizeOrigCol = 0;
let resizeOrigRow = 0;

function onEditResizeStart(line: DTOUserDashbordChartLine, direction: 'se' | 'e' | 's' | 'n' | 'w' | 'nw' | 'ne' | 'sw', e: MouseEvent) {
  if (!isEditMode.value) return;
  e.preventDefault();
  e.stopPropagation();
  resizingLine.value = line;
  resizeDirection.value = direction;
  dragStartX.value = e.clientX;
  dragStartY.value = e.clientY;
  resizeOrigCols.value = line.cols ?? 1;
  resizeOrigRows.value = line.rows ?? 1;
  resizeOrigCol = line.colNumber ?? 1;
  resizeOrigRow = line.rowNumber ?? 1;
  lockCursor(direction);
  document.addEventListener('mousemove', onEditResizeMove);
  document.addEventListener('mouseup', onEditResizeEnd);
}

function snapDelta(pixelDelta: number, cellSize: number): number {
  const sign = Math.sign(pixelDelta);
  const abs = Math.abs(pixelDelta);
  const threshold = cellSize * 0.2;
  return sign * Math.floor((abs + (cellSize - threshold)) / cellSize);
}

function onEditResizeMove(e: MouseEvent) {
  if (!resizingLine.value) return;
  const {cellWidth, cellHeight} = getCellDimensions();
  const dir = resizeDirection.value;
  const isRTL = Translator.isArabic();
  const hDir = isRTL ? -1 : 1;
  const rawDeltaX = e.clientX - dragStartX.value;
  const rawDeltaY = e.clientY - dragStartY.value;
  let changed = false;

  // End edge: grow/shrink cols from the end side (right in LTR, left in RTL)
  if (END_DIRS.has(dir)) {
    const deltaCols = snapDelta(hDir * rawDeltaX, cellWidth);
    const maxCols = getColsCount() - (resizingLine.value.colNumber ?? 1) + 1;
    const newCols = Math.max(1, Math.min(maxCols, resizeOrigCols.value + deltaCols));
    if (newCols !== resizingLine.value.cols) { resizingLine.value.cols = newCols; changed = true; }
  }

  // Start edge: move colNumber and adjust cols (left in LTR, right in RTL)
  if (START_DIRS.has(dir)) {
    const deltaCols = snapDelta(-hDir * rawDeltaX, cellWidth);
    const newCol = Math.max(1, resizeOrigCol - deltaCols);
    const actualDelta = resizeOrigCol - newCol;
    const newCols = Math.max(1, resizeOrigCols.value + actualDelta);
    if (newCols !== resizingLine.value.cols || newCol !== resizingLine.value.colNumber) {
      resizingLine.value.colNumber = newCol;
      resizingLine.value.cols = newCols;
      changed = true;
    }
  }

  // Bottom edge: grow/shrink rows from bottom
  if (BOTTOM_DIRS.has(dir)) {
    const deltaRows = snapDelta(rawDeltaY, cellHeight);
    const newRows = Math.max(1, resizeOrigRows.value + deltaRows);
    if (newRows !== resizingLine.value.rows) { resizingLine.value.rows = newRows; changed = true; }
  }

  // Top edge: move rowNumber and adjust rows
  if (TOP_DIRS.has(dir)) {
    const deltaRows = snapDelta(-rawDeltaY, cellHeight);
    const newRow = Math.max(1, resizeOrigRow - deltaRows);
    const actualDelta = resizeOrigRow - newRow;
    const newRows = Math.max(1, resizeOrigRows.value + actualDelta);
    if (newRows !== resizingLine.value.rows || newRow !== resizingLine.value.rowNumber) {
      resizingLine.value.rowNumber = newRow;
      resizingLine.value.rows = newRows;
      changed = true;
    }
  }

  if (changed) resolveCollisions(resizingLine.value);
}

function onEditResizeEnd() {
  if (resizingLine.value) {
    compactEmptyRows();
    scrollToWidget((resizingLine.value as any).widgetId);
  }
  resizingLine.value = null;
  unlockCursor();
  document.removeEventListener('mousemove', onEditResizeMove);
  document.removeEventListener('mouseup', onEditResizeEnd);
}

function removeWidget(line: DTOUserDashbordChartLine) {
  const lines = props.presenter.fetchedData.value?.chartLines;
  if (!lines) return;
  const idx = lines.indexOf(line);
  if (idx >= 0) {
    viewOptionsStore.forgetWidget(line.widgetId);
    lines.splice(idx, 1);
    compactEmptyRows();
  }
}

async function addWidget() {
  await widgetPickerPresenter.edit(
    [NamaCommon.createQuestionFieldRef("widgetId", "DashBoardWidget")],
    []
  );
  widgetPickerOpen.value = true;
}

async function onWidgetPickerConfirm() {
  const selectedRef = widgetPickerPresenter.fetchRecord().widgetId;
  if (!selectedRef?.id) return;

  widgetPickerOpen.value = false;

  const lines = props.presenter.fetchedData.value?.chartLines;
  if (!lines) return;

  if (lines.some((l: any) => l.widgetId === selectedRef.id)) return;

  const newLine: any = {
    widgetId: selectedRef.id,
    chartType: 'EChart',
      arChartTitle: selectedRef.name1,
      enChartTitle: selectedRef.name2,
    colNumber: 1,
    rowNumber: findNextAvailableRow(lines),
    cols: 3,
    rows: 2,
    _isNewlyAdded: true
  };
  lines.push(newLine);
  chartKey.value++;

  try {
    const response = await NamaCommon.namaFetch(NamaURLS.FetchSingleBIWidget, {
      data: {widgetId: selectedRef.id}
    });
    if (NamaCommon.isSuccess(response)) {
      const data = (response as any).data;
      if (data) {
        const savedPos = {colNumber: newLine.colNumber, rowNumber: newLine.rowNumber, cols: newLine.cols, rows: newLine.rows};
        Object.assign(newLine, data);
        Object.assign(newLine, savedPos);
        if (newLine.chartType === 'CrossFilterControl') {
          newLine.cols = 2;
          newLine.rows = 1;
        }
        delete newLine._isNewlyAdded;
        chartKey.value++;
      }
    }
  } catch { /* widget will show with title only */ }
}

function findNextAvailableRow(lines: DTOUserDashbordChartLine[]): number {
  let maxRow = 0;
  for (const line of lines) {
    const endRow = (line.rowNumber ?? 1) + (line.rows ?? 1);
    if (endRow > maxRow) maxRow = endRow;
  }
  return maxRow;
}

function compactEmptyRows() {
  const lines = props.presenter.fetchedData.value?.chartLines;
  if (!lines || lines.length === 0) return;

  // Repeat until no empty rows found
  for (let safety = 0; safety < 100; safety++) {
    const maxRow = findNextAvailableRow(lines);
    if (maxRow <= 1) break;

    // Build set of occupied rows (respecting row spans)
    const occupiedRows = new Set<number>();
    for (const line of lines) {
      const startRow = line.rowNumber ?? 1;
      const span = line.rows ?? 1;
      for (let r = startRow; r < startRow + span; r++)
        occupiedRows.add(r);
    }

    // Find the first empty row
    let emptyRow = -1;
    for (let r = 1; r < maxRow; r++) {
      if (!occupiedRows.has(r)) { emptyRow = r; break; }
    }
    if (emptyRow === -1) break;

    // Shift every widget whose rowNumber > emptyRow up by 1
    for (const line of lines) {
      if ((line.rowNumber ?? 1) > emptyRow)
        line.rowNumber = (line.rowNumber ?? 1) - 1;
    }
  }
}

function getEditablePositions(): DTOUserDashbordChartLine[] {
  compactEmptyRows();
  return props.presenter.fetchedData.value?.chartLines ?? [];
}

function normalizeLayout() {
  const lines = props.presenter.fetchedData.value?.chartLines;
  if (!lines || lines.length === 0) return;
  const sorted = [...lines].sort((a, b) => {
    const ra = a.rowNumber ?? 1;
    const rb = b.rowNumber ?? 1;
    if (ra !== rb) return ra - rb;
    return (a.colNumber ?? 1) - (b.colNumber ?? 1);
  });
  for (const line of sorted)
    resolveCollisions(line);
  compactEmptyRows();
}

const widgetPickerOpen = ref(false);
const widgetPickerPresenter = new QuestionFieldsPresenter();

defineExpose({isEditMode, getEditablePositions, normalizeLayout});
</script>

<template>
  <div class="tw-h-full tw-w-full tw-relative tw-flex tw-flex-col" style="--refresh-btn-height:40px;">
    <!-- Edit mode toolbar -->
    <div v-if="isEditMode"
      style="height:var(--refresh-btn-height)"
      class="tw-w-full tw-justify-center tw-sticky dark:tw-border-[#484848] tw-shrink-0 tw-border-gray-300 tw-border-b-[0.25px] tw-top-0 tw-z-[10] tw-flex tw-items-center tw-gap-2 tw-p-2 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark">
      <q-btn flat dense size="sm" icon="add" :label="Translator.translate('biAddWidget')" @click="addWidget"
             class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white tw-px-2 tw-rounded"/>
    </div>
    <CrossFilterBar :questionFields="crossFilterQuestionFields" @filtersChanged="onCrossFiltersChanged">
      <template #actions>
        <q-tabs v-if="isTabbedDashboard && dashboardTabs.length > 0"
                :model-value="activeTabId"
                @update:model-value="onTabSwitched"
                dense
                no-caps
                align="left"
                indicator-color="primary"
                active-color="primary"
                class="tw-shrink-0 tw-grow-0 text-grey">
          <q-tab v-for="tab in dashboardTabs" :key="tab.subDashboardId" :name="tab.subDashboardId" :label="tab.label"/>
        </q-tabs>
        <a v-if="canShowRefreshButton() && !isEditMode"
          :class="{'nama-toolbar-disabled !tw-bg-gray-300 dark:!tw-bg-gray-500':isRefreshClicked}"
          class="tw-bg-gray-300/90 active:tw-bg-gray-300 dark:tw-bg-gray-600 dark:active:tw-bg-gray-500 tw-p-1 tw-rounded-lg tw-cursor-pointer"
          @click.stop="onRefreshClicked" :title="Translator.translateId(BusinessFunction.refresh,'BusinessFunction')">
          <NamaIcon class="tw-size-5" :default-icon="isRefreshClicked?'nm-loading':'nm-refresh_2546743'"/>
        </a>
        <a v-if="showAdminToolbox && !isEditMode && presenter.dashboardId"
          class="tw-bg-gray-300/90 active:tw-bg-gray-300 dark:tw-bg-gray-600 dark:active:tw-bg-gray-500 tw-p-1 tw-rounded-lg tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
          @click.stop="openDashboardEditView" title="Open Dashboard">
          <q-icon name="open_in_new" size="20px"/>
        </a>
        <q-btn v-if="!isEditMode" flat dense round size="sm" icon="more_vert"
               :title="Translator.translate('biViewOptions')"
               class="tw-bg-gray-300/90 dark:tw-bg-gray-600">
          <q-menu anchor="bottom right" self="top right">
            <q-list dense style="min-width: 220px;">
              <q-item>
                <q-item-section>
                  <q-toggle :model-value="viewOptionsStore.showToolbox"
                            @update:model-value="(v: boolean) => viewOptionsStore.setShowToolbox(v)"
                            :label="Translator.translate('biShowToolbox')"
                            dense/>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </template>
    </CrossFilterBar>
    <div ref="dashboardContainerRef"
         class="tw-overflow-y-auto tw-grow tw-w-full">
      <div ref="gridRef"
           class="desktop:tw-grid mobile:tw-flex mobile:tw-flex-wrap mobile:tw-max-h-full mobile:tw-overflow-y-auto mobile:tw-grow mobile:tw-h-full mobile:tw-w-full"
           :style="dashboardContainerStyle">
      <template v-for="line in chartLines" :key="line.widgetId">
        <div class="tw-p-1 tw-max-w-full tw-overflow-hidden" :data-widget-id="line.widgetId"
             :style="{
               ...(Screen.lt.sm
                 ? { width: '100%', height: isAutoHeightWidget(line) ? undefined : calcMobileMaxRowsPrecent() + '%' }
                 : { gridArea: widgetGridArea(line, effectiveRows(line)),
                     minHeight: !isAutoHeightWidget(line) ? '0' : undefined }),
               ...getDragStyle(line)
             }"
        >
          <div v-if="line.chartType && isLineCollapsed(line)"
               class="tw-h-full tw-w-full tw-flex tw-items-center tw-justify-between tw-px-2 tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-rounded tw-shadow tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-cursor-pointer hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
               :title="Translator.translate('biExpandWidget')"
               @click="viewOptionsStore.toggleCollapsed(line.widgetId)">
            <span class="tw-text-sm tw-font-semibold tw-truncate tw-text-gray-700 dark:tw-text-gray-300">
              {{ getWidgetTitle(line) }}
            </span>
            <q-icon name="expand_more" size="20px" class="tw-text-gray-600 dark:tw-text-gray-400"/>
          </div>
          <div v-else-if="line.chartType" class="tw-p-0.5 tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-rounded tw-shadow tw-relative"
               :class="[isEditMode ? 'edit-mode-widget' : '', isAutoHeightWidget(line) ? '' : 'tw-h-full']">
            <span v-if="line.chartType === 'CrossFilterControl'
                          && !line.crossFilterControlQuestionField?.hideTitle
                          && Translator.translateTitledId(undefined, line.crossFilterControlQuestionField)"
                  class="cf-legend"
                  v-text="Translator.translateTitledId(undefined, line.crossFilterControlQuestionField)"/>
            <component :is="getDashboardWidgetComponent(line)" :chartLine="line" :isThreeD="getDashboardWidgetDefinition(line)?.isThreeD"
                       :isScheduler="getDashboardWidgetDefinition(line)?.isScheduler" :dashboardId="presenter.dashboardId"
                       :chartKey="chartKeyComputed"
                       @crossFilterClicked="onChartCrossFilterClicked"
                       @drillDownRequested="onDrillDownRequested"
                       @drillDownByRequested="onDrillDownByRequested"
                       @filtersChanged="onCrossFiltersChanged"/>
            <div v-if="!isEditMode && (canShowFloatingCollapse(line) || canShowFloatingOpenWidget(line) || line.localCrossFilterQuestionFields?.length)"
                 class="tw-absolute tw-top-1 tw-right-1 tw-z-10 tw-flex tw-items-center tw-gap-1">
              <q-btn v-if="canShowFloatingOpenWidget(line)"
                     flat dense round size="sm" icon="open_in_new"
                     title="Open widget edit screen (admin)"
                     class="tw-bg-white/80 dark:tw-bg-gray-800/80 tw-shadow"
                     @click="openWidgetEditView(line)"/>
              <q-btn v-if="canShowFloatingCollapse(line)"
                     flat dense round size="sm" icon="expand_less"
                     :title="Translator.translate('biCollapseWidget')"
                     class="tw-bg-white/80 dark:tw-bg-gray-800/80 tw-shadow"
                     @click="viewOptionsStore.toggleCollapsed(line.widgetId)"/>
              <WidgetLocalFilterButton v-if="line.localCrossFilterQuestionFields?.length"
                                       :widgetId="line.widgetId!"
                                       :questionFields="line.localCrossFilterQuestionFields"
                                       @filtersChanged="onCrossFiltersChanged"/>
            </div>
            <!-- Edit mode overlay -->
            <div v-if="isEditMode" class="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-flex-col"
                 style="background: rgba(0,0,0,0.03);">
              <!-- Drag handle -->
              <div class="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1 tw-cursor-move tw-bg-indigo-500/80 tw-text-white tw-text-xs tw-rounded-t tw-select-none"
                   @mousedown="onEditDragStart(line, $event)">
                <span class="tw-truncate">{{ getWidgetTitle(line) }}</span>
                <q-btn flat dense round size="xs" icon="close" class="tw-text-white tw-opacity-80 hover:tw-opacity-100"
                       @click.stop="removeWidget(line)" :title="Translator.translate('biRemoveWidget')"/>
              </div>
              <!-- Transparent body (blocks chart clicks) -->
              <div class="tw-grow"/>
              <!-- Resize handles — corners -->
              <div class="edit-handle edit-handle-corner tw-bottom-0 tw-end-0 tw-rounded-tl"
                   :style="{cursor: resizeCursorFor('se')}"
                   @mousedown="onEditResizeStart(line, 'se', $event)"/>
              <div class="edit-handle edit-handle-corner tw-bottom-0 tw-start-0 tw-rounded-tr"
                   :style="{cursor: resizeCursorFor('sw')}"
                   @mousedown="onEditResizeStart(line, 'sw', $event)"/>
              <div class="edit-handle edit-handle-corner tw-top-0 tw-end-0 tw-rounded-bl"
                   :style="{cursor: resizeCursorFor('ne')}"
                   @mousedown="onEditResizeStart(line, 'ne', $event)"/>
              <div class="edit-handle edit-handle-corner tw-top-0 tw-start-0 tw-rounded-br"
                   :style="{cursor: resizeCursorFor('nw')}"
                   @mousedown="onEditResizeStart(line, 'nw', $event)"/>
              <!-- Resize handles — edges -->
              <div class="edit-handle tw-bottom-0 tw-start-1/2 tw-w-8 tw-h-2 tw--translate-x-1/2 tw-rounded-t tw-cursor-ns-resize"
                   @mousedown="onEditResizeStart(line, 's', $event)"/>
              <div class="edit-handle tw-top-0 tw-start-1/2 tw-w-8 tw-h-2 tw--translate-x-1/2 tw-rounded-b tw-cursor-ns-resize"
                   @mousedown="onEditResizeStart(line, 'n', $event)"/>
              <div class="edit-handle tw-top-1/2 tw-end-0 tw-w-2 tw-h-8 tw--translate-y-1/2 tw-rounded-s tw-cursor-ew-resize"
                   @mousedown="onEditResizeStart(line, 'e', $event)"/>
              <div class="edit-handle tw-top-1/2 tw-start-0 tw-w-2 tw-h-8 tw--translate-y-1/2 tw-rounded-e tw-cursor-ew-resize"
                   @mousedown="onEditResizeStart(line, 'w', $event)"/>
            </div>
          </div>
        </div>
      </template>
      </div>
    </div>
    <DrillDownDialog v-if="(drillDownClick || drillDownByClick) && drillDownDialogOpen"
                     v-model="drillDownDialogOpen"
                     :dashboardId="presenter.dashboardId"
                     :rootClick="drillDownClick"
                     :rootDrillByClick="drillDownByClick"/>
    <q-dialog v-model="widgetPickerOpen">
      <q-card class="tw-w-[500px] tw-max-w-[90vw] dark:tw-bg-namaBackground-bright-dark">
        <q-bar class="tw-flex-none tw-h-fit tw-min-h-8 tw-text-namaFontColor-input-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] dark:tw-bg-[#212529] dark:tw-text-white">
          <span v-text="Translator.translate('biAddWidget')"/>
          <q-space/>
          <q-btn dense flat icon="close" v-close-popup/>
        </q-bar>
        <q-card-section class="tw-min-h-[80px]">
          <QuestionFieldsEditor :presenter="widgetPickerPresenter" :singleColumnGroup="true" :doNotApplyThemeLayouts="true"/>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat v-close-popup>{{ Translator.translate('cancel') }}</q-btn>
          <q-btn color="primary" @click="onWidgetPickerConfirm">{{ Translator.translate('ok') }}</q-btn>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style scoped>
:deep([data-animate-icon]),
:deep([data-animate-box]),
:deep([data-animate-badge]),
:deep([data-animate-text]) {
  opacity: 0;
  translate: 0 -20px;
}

.edit-mode-widget {
  outline: 2px dashed rgba(99, 102, 241, 0.4);
  outline-offset: -2px;
  transition: outline-color 0.15s;
}

.edit-mode-widget:hover {
  outline-color: rgba(99, 102, 241, 0.8);
}

.edit-handle {
  position: absolute;
  background: rgb(99 102 241);
  opacity: 0.5;
  z-index: 25;
}

.edit-handle:hover {
  opacity: 0.9;
}

.edit-handle-corner {
  width: 12px;
  height: 12px;
}

.cf-legend {
  @apply tw-absolute -tw-top-2 tw-left-3 tw-px-1.5 tw-text-sm tw-font-semibold tw-z-[1] tw-pointer-events-none tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-max-w-[calc(100%_-_24px)] tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark;
}
</style>
